import { classInstance } from '@/shared/helpers/helpers';
import { Callbacks, Form } from '@/shared/form/form';

/** @inheritdoc */
export interface AuthorizationCallbacks extends Callbacks {
	/**
	 * Коллбэк, выполняющийся при успешной отправке формы. Возвращает Promise.
	 * @category 6 Авторизация
	 * @return {Promise<CallbackResult>} Отдаёт объект с успешным статусом и, опционально, дополнительными данными
	 * @throws {Promise<CallbackError>} Отдаёт объект с неудачным статусом, списком ошибок и, опционально, дополнительными данными
	 */
	onFormSubmit?: () => Promise<object>;
}

/**
 * @desc Форма авторизации
 * @category 6 Авторизация
 * @example
 * window.app.authorizationForm = new Form(document.querySelector('.js-authorization-form'));
 * @constructor
 * @param {HTMLFormElement} element HTML-элемент <form>
 * @param {Callbacks} callbacks объект типа Callbacks
 */

export class AuthorizationForm {
	readonly element: HTMLElement;
	protected callbacks: AuthorizationCallbacks;
	private form: Form | null;

	constructor(element: HTMLElement, callbacks?: AuthorizationCallbacks) {
		if (!element) return;
		this.element = element;
		this.callbacks = callbacks;
		this.init();
	}

	/**
	 * Заготовка для обработчика formSubmit.
	 */
	protected handleFormSubmit(): void {
		if (this.callbacks?.onFormSubmit) {
			this.callbacks.onFormSubmit().then(
				(result) => (result.json ? result.json() : result),
				(error) => (error?.json ? error.json() : error),
			);
		}
	}

	/**
	 * @desc Добавление коллбэков после создания формы.
	 * @param {Callbacks} callbacks объект типа Callbacks.
	 * @example
	 * const myForm = app.classInstance.get(document.querySelector('.js-authorization-form'));
	 * myForm.authorizationForm.addCallbacks({ onSubmit });
	 */
	public addCallbacks = (callbacks: Callbacks): void => {
		this.callbacks = { ...this.callbacks, ...callbacks };
		this.form.addCallbacks(this.callbacks);
	};

	/**
	 * Инициализация экземпляра:
	 * * элементы, не создаваемые напрямую из аргументов конструктора;
	 * * обработчики событий.
	 */
	public init(): void {
		this.form = classInstance.get(this.element).form;
		this.form.addCallbacks(this.callbacks);
		this.element.addEventListener('formSubmit', this.handleFormSubmit);
		classInstance.set(this.element, { authorizationForm: this });
	}

	/**
	 * Уничтожение экземпляра:
	 * * обнуление элементов;
	 * * удаление обработчиков.
	 */
	public destroy = (): void => {
		this.form = null;
		this.element.removeEventListener('formSubmit', this.handleFormSubmit);
		classInstance.del(this.element, 'authorizationForm');
	};
}
